import { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { conferenciaApi, type ConferenciaListItem } from '../api/conferencia';

export interface ConferenciasFilters {
  search?: string;
  status?: 'TODOS' | 'EM_ANDAMENTO' | 'CONCLUIDO' | 'CANCELADO';
  dataInicio?: string;
  dataFim?: string;
  usuario?: string;
  page?: number;
  pageSize?: number;
}

export interface ConferenciasStats {
  total: number;
  emAndamento: number;
  concluidas: number;
  canceladas: number;
  percentualConclusao: number;
}

export const useConferencias = (filters: ConferenciasFilters = {}) => {
  const queryClient = useQueryClient();

  // Query para buscar conferências
  const {
    data: conferencias = [],
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: ['conferencias', filters],
    queryFn: async () => {
      const data = await conferenciaApi.listarConferencias();
      return data || [];
    },
    staleTime: 30000, // Cache por 30 segundos
    refetchInterval: 60000, // Auto-refresh a cada minuto
  });

  // Mutation para excluir conferência
  const excluirMutation = useMutation({
    mutationFn: ({ empresa, codigoId }: { empresa: string; codigoId: string }) =>
      conferenciaApi.removerConferencia(empresa, codigoId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['conferencias'] });
    },
  });

  // Filtrar conferências baseado nos filtros
  const conferenciasFiltradas = useMemo(() => {
    let resultado = [...conferencias];

    // Filtro por texto
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      resultado = resultado.filter(conf =>
        conf.numeroNfe?.toLowerCase().includes(searchLower) ||
        conf.fornecedor?.toLowerCase().includes(searchLower) ||
        conf.codigoId.toLowerCase().includes(searchLower) ||
        conf.usuario?.toLowerCase().includes(searchLower)
      );
    }

    // Filtro por status
    if (filters.status && filters.status !== 'TODOS') {
      resultado = resultado.filter(conf => conf.status === filters.status);
    }

    // Filtro por data
    if (filters.dataInicio) {
      resultado = resultado.filter(conf =>
        new Date(conf.dataConferencia) >= new Date(filters.dataInicio!)
      );
    }

    if (filters.dataFim) {
      resultado = resultado.filter(conf =>
        new Date(conf.dataConferencia) <= new Date(filters.dataFim!)
      );
    }

    // Filtro por usuário
    if (filters.usuario) {
      resultado = resultado.filter(conf =>
        conf.usuario?.toLowerCase().includes(filters.usuario!.toLowerCase())
      );
    }

    return resultado;
  }, [conferencias, filters]);

  // Estatísticas
  const stats: ConferenciasStats = useMemo(() => {
    const total = conferenciasFiltradas.length;
    const emAndamento = conferenciasFiltradas.filter(c => c.status === 'EM_ANDAMENTO').length;
    const concluidas = conferenciasFiltradas.filter(c => c.status === 'CONCLUIDO').length;
    const canceladas = conferenciasFiltradas.filter(c => c.status === 'CANCELADO').length;
    const percentualConclusao = total > 0 ? Math.round((concluidas / total) * 100) : 0;

    return {
      total,
      emAndamento,
      concluidas,
      canceladas,
      percentualConclusao
    };
  }, [conferenciasFiltradas]);

  // Agrupar por status (útil para mobile)
  const conferenciasPorStatus = useMemo(() => {
    const grupos: Record<string, ConferenciaListItem[]> = {};
    conferenciasFiltradas.forEach(conf => {
      const status = conf.status || 'SEM_STATUS';
      if (!grupos[status]) {
        grupos[status] = [];
      }
      grupos[status].push(conf);
    });
    return grupos;
  }, [conferenciasFiltradas]);

  // Paginação
  const totalPages = Math.ceil(conferenciasFiltradas.length / (filters.pageSize || 10));
  const currentPage = filters.page || 1;
  const startIndex = (currentPage - 1) * (filters.pageSize || 10);
  const endIndex = startIndex + (filters.pageSize || 10);
  const conferenciasPaginadas = conferenciasFiltradas.slice(startIndex, endIndex);

  // Lista de usuários únicos (para filtros)
  const usuarios = useMemo(() => {
    const usuariosSet = new Set(conferencias.map(c => c.usuario).filter(Boolean));
    return Array.from(usuariosSet).sort();
  }, [conferencias]);

  return {
    // Dados
    conferencias: conferenciasFiltradas,
    conferenciasPaginadas,
    conferenciasPorStatus,
    stats,
    usuarios,
    
    // Estados
    isLoading,
    error: error as Error | null,
    
    // Paginação
    totalPages,
    currentPage,
    
    // Ações
    refetch,
    excluir: excluirMutation.mutate,
    isExcluindo: excluirMutation.isPending,
  };
};